'use client';

import { useMemo, useSyncExternalStore } from 'react';

import type { AuthMethod } from '../components/auth-method-button';
import {
  LAST_AUTH_METHOD_KEY,
  type LastAuthMethod,
} from '../utils/last-auth-method';

const emptySubscribe = () => () => {};

// Computed once at module load — page-load precision is plenty for a 30-day
// cutoff, and render code must stay pure (react-hooks/purity forbids
// Date.now() inside the memo).
const LAST_AUTH_METHOD_CUTOFF = Date.now() - 30 * 24 * 60 * 60 * 1000;

// Smart default shared by the web methods containers AND the native auth
// pages: the method the user last signed in with (matches the
// LastAuthMethodHint copy above the web form). Read via useSyncExternalStore
// so the server render and the hydration render both see null (no mismatch)
// and React swaps in the client value right after hydration — the sanctioned
// pattern for localStorage-backed values, with no setState-in-effect.
// Snapshots the RAW string (stable primitive); parsing happens in a memo.
export function useLastAuthMethodPreference(): AuthMethod | null {
  const raw = useSyncExternalStore(
    emptySubscribe,
    () => {
      try {
        return window.localStorage.getItem(LAST_AUTH_METHOD_KEY);
      } catch {
        return null;
      }
    },
    () => null,
  );

  return useMemo(() => {
    if (!raw) return null;

    try {
      const last = JSON.parse(raw) as LastAuthMethod;

      // Mirror getLastAuthMethod()'s 30-day expiry (read-only — no removal).
      if (!last.timestamp || last.timestamp < LAST_AUTH_METHOD_CUTOFF) {
        return null;
      }

      // Map the persisted record onto a switcher method. Both the phone and
      // email-code flows record 'otp'; phone flows store the E.164 phone
      // (leading '+') in the record's email field, which disambiguates them.
      // OAuth maps to nothing — its buttons render separately below.
      if (last.method === 'password') return 'password';
      if (last.method === 'magic_link') return 'magicLink';

      if (last.method === 'otp') {
        return last.email?.startsWith('+') ? 'phone' : 'otp';
      }

      return null;
    } catch {
      return null;
    }
  }, [raw]);
}
